import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Building2, Save, Hash, LayoutGrid, Contact } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { setAccountCode, setNumberingPreference, updateOrgProfile } from "../services/profiles";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { COUNTRIES } from "../lib/countries";

export default function LabAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [code, setCode] = useState(profile?.accountCode || "");
  const [saving, setSaving] = useState(false);
  const [numbering, setNumbering] = useState(profile?.numberingPreference || "fdi");
  const [savingPref, setSavingPref] = useState(false);

  const [org, setOrg] = useState({
    accountType: profile?.accountType || "",
    accountTypeOther: profile?.accountTypeOther || "",
    name: profile?.name || "",
    contactName: profile?.contactName || "",
    phone: profile?.phone || "",
    street: profile?.address?.street || "",
    city: profile?.address?.city || "",
    state: profile?.address?.state || "",
    postalCode: profile?.address?.postalCode || "",
    country: profile?.address?.country || "",
    website: profile?.website || "",
    description: profile?.description || "",
  });
  const [savingOrg, setSavingOrg] = useState(false);
  const setO = (k, v) => setOrg((prev) => ({ ...prev, [k]: v }));

  const saveOrg = async () => {
    setSavingOrg(true);
    try {
      await updateOrgProfile(user.uid, {
        accountType: org.accountType,
        accountTypeOther: org.accountType === "Other" ? org.accountTypeOther : "",
        name: org.name,
        contactName: org.contactName,
        phone: org.phone,
        website: org.website,
        description: org.description,
        address: { street: org.street, city: org.city, state: org.state, postalCode: org.postalCode, country: org.country },
      });
      await refreshProfile();
      toast.success(t("org.saved"));
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setSavingOrg(false);
    }
  };

  const sanitize = (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);

  const save = async () => {
    if (code.length < 2) {
      toast.error(t("account.tooShort"));
      return;
    }
    setSaving(true);
    try {
      await setAccountCode(user.uid, code);
      await refreshProfile();
      toast.success(t("account.saved"));
      navigate("/lab");
    } catch (e) {
      if (e.code === "taken") toast.error(t("account.taken"));
      else toast.error(e?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const savePref = async () => {
    setSavingPref(true);
    try {
      await setNumberingPreference(user.uid, numbering);
      await refreshProfile();
      toast.success(t("account.prefSaved"));
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setSavingPref(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="flex items-center gap-2 font-heading text-3xl font-bold tracking-tight">
        <Building2 className="h-7 w-7" /> {t("account.title")}
      </h1>
      <p className="mt-1 text-muted-foreground">{t("account.subtitle")}</p>

      <div className="mt-8 space-y-4 rounded-lg border border-border bg-card p-6">
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5"><Hash className="h-4 w-4" /> {t("account.accountCode")}</Label>
          <Input
            value={code}
            onChange={(e) => setCode(sanitize(e.target.value))}
            placeholder="E.G. BSDL"
            className="font-mono uppercase tracking-widest"
            data-testid="account-code-input"
          />
          <p className="text-xs text-muted-foreground">{t("account.accountCodeHint")}</p>
          {code && (
            <p className="text-xs text-muted-foreground">
              {t("account.preview")}: <span className="font-mono font-semibold text-foreground">DCC-{code}-{new Date().getFullYear()}-00001</span>
            </p>
          )}
        </div>
        <Button className="w-full rounded-full bg-brand text-white hover:bg-brand/90" onClick={save} disabled={saving} data-testid="save-account-code">
          <Save className="mr-1.5 h-4 w-4" /> {t("account.save")}
        </Button>
      </div>

      <div className="mt-6 space-y-4 rounded-lg border border-border bg-card p-6" data-testid="numbering-pref-section">
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5"><LayoutGrid className="h-4 w-4" /> {t("account.numberingPref")}</Label>
          <p className="text-xs text-muted-foreground">{t("account.numberingPrefHint")}</p>
          <div className="mt-2 flex rounded-md border border-input p-0.5" data-testid="pref-numbering-toggle">
            {["fdi", "universal"].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setNumbering(n)}
                data-testid={`pref-numbering-${n}`}
                className={`flex-1 rounded px-2 py-2 text-sm font-medium transition-colors ${numbering === n ? "bg-brand text-white" : "text-muted-foreground hover:bg-secondary"}`}
              >
                {t(`odontogram.${n}`)}
              </button>
            ))}
          </div>
        </div>
        <Button variant="secondary" className="w-full rounded-full" onClick={savePref} disabled={savingPref} data-testid="save-numbering-pref">
          <Save className="mr-1.5 h-4 w-4" /> {t("account.savePref")}
        </Button>
      </div>

      <div className="mt-6 space-y-4 rounded-lg border border-border bg-card p-6" data-testid="org-profile-section">
        <div>
          <Label className="flex items-center gap-1.5 text-base font-semibold"><Contact className="h-4 w-4" /> {t("org.title")}</Label>
          <p className="text-xs text-muted-foreground">{t("org.subtitle")}</p>
        </div>

        <div className="space-y-1.5">
          <Label>{t("org.accountEmail")}</Label>
          <Input value={user?.email || ""} readOnly disabled className="cursor-not-allowed opacity-80" data-testid="org-account-email" />
          <p className="text-xs text-muted-foreground">{t("org.accountEmailHint")}</p>
        </div>

        <div className="space-y-1.5">
          <Label>{t("register.accountType")}</Label>
          <Select value={org.accountType} onValueChange={(v) => setO("accountType", v)}>
            <SelectTrigger data-testid="org-account-type"><SelectValue placeholder={t("register.selectAccountType")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Dental Laboratory">{t("accountTypes.lab")}</SelectItem>
              <SelectItem value="Dental Clinic">{t("accountTypes.clinic")}</SelectItem>
              <SelectItem value="Independent Dentist">{t("accountTypes.dentist")}</SelectItem>
              <SelectItem value="Other">{t("accountTypes.other")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {org.accountType === "Other" && (
          <div className="space-y-1.5">
            <Label>{t("register.specify")}</Label>
            <Input value={org.accountTypeOther} onChange={(e) => setO("accountTypeOther", e.target.value)} data-testid="org-account-type-other" />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>{t("register.businessName")}</Label>
          <Input value={org.name} onChange={(e) => setO("name", e.target.value)} data-testid="org-name" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("register.contactPerson")}</Label>
          <Input value={org.contactName} onChange={(e) => setO("contactName", e.target.value)} data-testid="org-contact-name" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("register.phone")}</Label>
          <Input value={org.phone} onChange={(e) => setO("phone", e.target.value)} data-testid="org-phone" />
        </div>
        <div className="space-y-1.5">
          <Label>{t("register.country")}</Label>
          <Select value={org.country} onValueChange={(v) => setO("country", v)}>
            <SelectTrigger data-testid="org-country"><SelectValue placeholder={t("register.selectCountry")} /></SelectTrigger>
            <SelectContent className="max-h-64">
              {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("register.street")}</Label>
          <Input value={org.street} onChange={(e) => setO("street", e.target.value)} data-testid="org-street" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("register.city")}</Label>
            <Input value={org.city} onChange={(e) => setO("city", e.target.value)} data-testid="org-city" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("register.state")}</Label>
            <Input value={org.state} onChange={(e) => setO("state", e.target.value)} data-testid="org-state" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>{t("register.postal")}</Label>
          <Input value={org.postalCode} onChange={(e) => setO("postalCode", e.target.value)} data-testid="org-postal" />
        </div>

        <div className="border-t border-border pt-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("org.credibilityOptional")}</p>
          <div className="space-y-1.5">
            <Label>{t("org.website")}</Label>
            <Input value={org.website} onChange={(e) => setO("website", e.target.value)} placeholder="https://" data-testid="org-website" />
          </div>
          <div className="mt-3 space-y-1.5">
            <Label>{t("org.description")}</Label>
            <Textarea rows={3} value={org.description} onChange={(e) => setO("description", e.target.value)} data-testid="org-description" />
          </div>
        </div>

        <Button className="w-full rounded-full bg-brand text-white hover:bg-brand/90" onClick={saveOrg} disabled={savingOrg} data-testid="save-org-profile">
          <Save className="mr-1.5 h-4 w-4" /> {t("org.save")}
        </Button>
      </div>
    </div>
  );
}
